<template>
  <div>
    <h2 class="primary--text mb-3">
      Locations
    </h2>

    <v-row>
      <v-col cols="12">
        <v-card
          outlined
          class="px-4"
        >
          <v-row justify="start">
            <v-col
              cols="6"
              md="4"
            >
              <v-autocomplete
                v-model="filters.retailer"
                :items="retailerAutocomplete.items"
                :loading="retailerAutocomplete.isLoading"
                clearable
                return-object
                item-value="id"
                item-text="name"
                label="Retailers"
                prepend-inner-icon="mdi-truck-outline"
                @change="fetchStores"
              />
            </v-col>

            <v-col
              align-self="center"
              cols="6"
              md="4"
              class="py-0"
            >
              <v-text-field
                v-model="filters.locationId"
                prepend-inner-icon="mdi-map-marker-outline"
                label="Search by location ID"
                @change="fetchStores"
              />
            </v-col>

            <v-col
              align-self="center"
              cols="6"
              md="4"
              class="py-0"
            >
              <v-text-field
                v-model="filters.postcode"
                prepend-inner-icon="mdi-magnify"
                label="Search by Postcode"
                @change="fetchStores"
              />
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <v-col
        class="pb-0"
        align-self="center"
        cols="12"
        md="6"
      >
        <div class="headline primary--text">
          <span
            v-if="filters.retailer && filters.retailer.name"
          >Store locations for {{ filters.retailer.name }} retailer</span>
          <span v-else>Store locations for all retailers</span>
        </div>
      </v-col>

      <v-col cols="12">
        <v-card>
          <v-data-table
            id="table"
            :items="stores"
            :headers="headers"
            :loading="isLoading"
            :server-items-length="1"
            :items-per-page="dataTableFilters.rowsPerPage"
            item-key="id"
            single-expand
            hide-default-footer
            @update:sort-by="handleSort"
            @update:sort-desc="handleSortDesc"
          >
            <template v-slot:item="props">
              <tr>
                <td
                  width="2%"
                  class="pr-0"
                >
                  <v-icon
                    class="mb-1"
                    color="primary"
                    @click="toggleExpand(props)"
                    v-text="
                      props.isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'
                    "
                  />
                </td>
                <td class="text-no-wrap">
                  {{ props.item.location_id || 'N/A' }}
                </td>
                <td class="py-2 lg">
                  <div class="mb-1">
                    {{ props.item.formatted_address || 'N/A' }}
                  </div>
                  <div class="grey--text caption">
                    <span class="mr-3">
                      Parking
                      <v-icon
                        v-if="props.item.parking"
                        size="13"
                        color="success lighten-1"
                      >mdi-check-circle</v-icon>
                      <v-icon
                        v-else
                        size="13"
                        color="red lighten-2"
                      >mdi-close-circle</v-icon>
                    </span>
                    <span>
                      Artic
                      <v-icon
                        v-if="props.item.arctic"
                        size="13"
                        color="success lighten-1"
                      >mdi-check-circle</v-icon>
                      <v-icon
                        v-else
                        size="13"
                        color="red lighten-2"
                      >mdi-close-circle</v-icon>
                    </span>
                  </div>
                </td>
                <td
                  class="text-no-wrap s-lg"
                >
                  {{ humanizeDateFromSeconds(props.item.updatedAt, 'Do MMM YYYY') || 'N/A' }}
                </td>
                <td class="md">
                  {{ props.item.retailerRef.id || 'N/A' }}
                </td>
                <td class="text-no-wrap">
                  {{ props.item.status || 'N/A' }}
                </td>
                <td class="text-center">
                  <v-icon
                    v-if="props.item.is_valid"
                    color="success"
                  >
                    mdi-check
                  </v-icon>
                  <v-icon
                    v-else
                    color="red"
                  >
                    mdi-close
                  </v-icon>
                </td>
                <td class="text-center">
                  <v-icon
                    v-if="props.item.has_time_windows"
                    color="success"
                  >
                    mdi-calendar-check-outline
                  </v-icon>
                  <v-icon
                    v-else
                    color="red"
                  >
                    mdi-calendar-remove-outline
                  </v-icon>
                </td>
                <td
                  width="5%"
                  class="text-center"
                >
                  <div class="d-flex align-center justify-center">
                    <v-btn
                      outlined
                      small
                      color="success"
                      class="mx-2"
                      @click="openChangeRequestForm(props.item)"
                    >
                      Request Change
                    </v-btn>
                  </div>
                </td>
              </tr>
            </template>

            <template v-slot:expanded-item="props">
              <td
                class="caption"
                :colspan="headers.length + 1"
              >
                <v-row
                  class="px-3"
                  :style="'width:' + tableWidth +'px;overflow: auto'"
                >
                  <template v-if="timeWindows.isLoading">
                    <v-col
                      class="pa-5 text-center"
                      cols="12"
                    >
                      <v-progress-circular
                        indeterminate
                        color="primary"
                      />
                    </v-col>
                  </template>

                  <template v-else-if="!timeWindows.list.length">
                    <v-col
                      class="pt-5 text-center"
                      cols="6"
                      md="9"
                    >
                      <div class="d-flex justify-center align-center text-center grey--text">
                        <div>
                          <v-icon
                            color="grey"
                            size="50"
                          >
                            mdi-text-box-search-outline
                          </v-icon>
                          <div class="subtitle-1 black--text my-1">
                            No Time Windows Available
                          </div>
                          <div class="caption my-1">
                            Please filter by a different date
                          </div>
                        </div>
                      </div>
                    </v-col>

                    <v-col
                      class="ml-auto pb-5 text-right"
                      cols="6"
                      md="3"
                    >
                      <h-date-range-picker
                        v-model="timeWindows.dateRange"
                        label="Filter by Date"
                        @input="fetchTimeWindows(props.item)"
                      />
                    </v-col>
                  </template>

                  <template v-else>
                    <v-col
                      md="9"
                      cols="6"
                      align-self="end"
                      class="px-5 subtitle-1 primary--text font-weight-bold"
                      v-text="
                        'The following time windows are available for the selected time range'"
                    />

                    <v-col
                      class="text-right"
                      cols="6"
                      md="3"
                    >
                      <h-date-range-picker
                        v-model="timeWindows.dateRange"
                        hide-details
                        label="Filter by Date"
                        @input="fetchTimeWindows(props.item)"
                      />
                    </v-col>

                    <template v-for="date in timeWindows.list">
                      <v-col
                        v-if="date.time_windows.length"
                        :key="date.for_date"
                        xs="12"
                        sm="6"
                        md="4"
                        lg="4"
                      >
                        <v-card
                          class="d-flex flex-column"
                          height="100%"
                        >
                          <v-card-title class="grey--text caption font-weight-bold grey lighten-4">
                            <span class="primary--text">{{ humanizeDate(date.for_date) }}</span>
                            <v-spacer />
                            <div
                              style="font-size: 10px"
                              class="grey--text"
                            >
                              <span class="grey--text">Updated:</span> &nbsp;
                              <span
                                class="grey--text"
                              >
                                {{ humanizeDateFromSeconds(date.last_updated, 'hh:mm DD/MM/YYYY') }}
                              </span>
                            </div>
                          </v-card-title>

                          <v-divider />

                          <v-card-text
                            class="py-0 pr-8 pl-0"
                            style="max-height:300px;overflow:auto"
                          >
                            <v-timeline dense>
                              <v-timeline-item
                                v-for="time_window in date.time_windows"
                                :key="time_window.quarter"
                                small
                              >
                                <v-card
                                  outlined
                                  class="indigo lighten-5 d-flex
                                  justify-center align-center pa-2 rounded-lg caption"
                                >
                                  <strong class="primary--text">
                                    {{ humanizeDateFromSeconds(time_window.start) }}
                                    -
                                    {{ humanizeDateFromSeconds(time_window.end) }}
                                  </strong>
                                  <v-icon
                                    color="green"
                                    small
                                    right
                                  >
                                    mdi-checkbox-marked-circle
                                  </v-icon>
                                </v-card>
                              </v-timeline-item>
                            </v-timeline>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </template>
                  </template>
                </v-row>
                <v-divider class="mt-2" />
              </td>
            </template>
          </v-data-table>
          <v-divider />

          <div class="d-flex pa-3">
            <v-spacer />
            <v-btn
              :disabled="dataTableFilters.currentPage === 1"
              icon
              class="mx-3"
              @click="previousPage"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn
              :disabled="stores.length < dataTableFilters.rowsPerPage"
              icon
              class="mx-3"
              @click="nextPage"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar
      v-model="snackbar.isOpen"
      :color="snackbar.color"
    >
      {{ snackbar.text }}
    </v-snackbar>

    <v-dialog
      v-if="changeRequestForm.isOpen"
      :value="true"
      :max-width="$vuetify.breakpoint.mdAndUp ? '55%': '90%'"
      persistent
    >
      <request-change-form
        :store="changeRequestForm.store"
        :is-loading="changeRequestForm.isLoading"
        @close="resetChangeRequestForm"
        @update="requestStoreUpdate"
        @remove="requestStoreRemove"
      />
    </v-dialog>
  </div>
</template>

<script>
import { storeRequest } from '@/endpoints';
import { fetchTimeWindows } from '@/utils/timeWindows';
import { storesCollection, retailerCollection } from '@/firebase';
import { parseStore, parseRetailer } from '@/utils/documentParser';
import { humanizeDateFromSeconds, humanizeDate } from '@/utils/misc';

import moment from 'moment';
import dataTableMixin from '@/mixins/dataTable';
import HDateRangePicker from '@/components/commons/HDateRangePicker.vue';
import RequestChangeForm from '@/components/location-manager/RequestChangeForm.vue';

const defaultTimeWindowsData = {
  list: [],
  isLoading: false,
  dateRange: {
    startDate: moment().startOf('week').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
  },
};

export default {
  components: { HDateRangePicker, RequestChangeForm },
  mixins: [dataTableMixin],
  data() {
    return {
      filters: {
        retailer: {},
        postcode: '',
        locationId: '',
      },
      isLoading: false,
      snackbar: {
        text: '',
        isOpen: false,
        color: 'green',
      },
      headers: [
        { text: '', sortable: false },
        { text: 'Location ID', value: 'location_id', sortable: false },
        { text: 'Address', value: 'address.street' },
        { text: 'Last Updated', value: 'last_updated' },
        { text: 'Retailer ID', value: 'retailer', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        {
          text: 'Is Valid?',
          value: 'is_valid',
          align: 'center',
          sortable: false,
        },
        {
          text: 'Has Time Windows?',
          value: 'has_time_windows',
          align: 'center',
          sortable: false,
        },
        { text: 'Actions', align: 'center', sortable: false },
      ],
      retailerAutocomplete: {
        items: [],
        isLoading: true,
      },
      changeRequestForm: {
        isOpen: false,
        isLoading: false,
        store: undefined,
      },
      storeDocs: [],
      timeWindows: { ...defaultTimeWindowsData },
    };
  },
  computed: {
    stores() {
      return this.storeDocs.map(parseStore);
    },
    tableWidth() {
      return document.getElementById('table').offsetWidth;
    },
  },
  created() {
    this.inititlizeDataTable({
      apiData: 'storeDocs',
      sortBy: 'last_updated',
      apiCallback: 'fetchStores',
    });
  },
  mounted() {
    this.fetchStores();
    this.fetchRetailers();
  },
  methods: {
    humanizeDate,
    humanizeDateFromSeconds,
    fetchRetailers() {
      this.retailerAutocomplete.isLoading = true;
      retailerCollection
        .get()
        .then((querySnapshot) => {
          this.retailerAutocomplete.items = querySnapshot.docs.map(
            parseRetailer,
          );
          this.retailerAutocomplete.isLoading = false;
        })
        .catch(() => {
          this.retailerAutocomplete.isLoading = false;
        });
    },
    fetchStores() {
      this.isLoading = true;

      let query = this.generateFilters(storesCollection);
      query = this.getPaginationQuery(query);

      query
        .get()
        .then((querySnapshot) => {
          this.storeDocs = querySnapshot.docs;
          this.isLoading = false;
        })
        .catch(() => {
          this.displaySnackbar({
            text: 'An error occured. Please try again',
            color: 'red',
          });
          this.isLoading = false;
        });
    },
    generateFilters(originalQuery) {
      let query = originalQuery;

      if (this.filters.locationId) {
        query = query.where('location_id', '==', this.filters.locationId);
      }

      if (this.filters.postcode) {
        query = query.where('address.postcode', '==', this.filters.postcode);
      }

      if (this.filters.retailer && this.filters.retailer.id) {
        query = query.where(
          'retailer',
          '==',
          retailerCollection.doc(this.filters.retailer.id),
        );
      }

      return query;
    },
    toggleExpand(props) {
      if (props.isExpanded) {
        props.expand(!props.isExpanded);
        this.timeWindows = { ...defaultTimeWindowsData };
        return;
      }

      props.expand(!props.isExpanded);
      this.fetchTimeWindows(props.item);
    },
    fetchTimeWindows(store) {
      this.timeWindows.isLoading = true;

      fetchTimeWindows(
        storesCollection.doc(store.id),
        this.timeWindows.dateRange.startDate,
        this.timeWindows.dateRange.endDate,
      )
        .then((response) => {
          this.timeWindows.list = [...response].filter(
            (date) => date.time_windows.length,
          );
          this.timeWindows.isLoading = false;
        })
        .catch(() => {
          this.timeWindows.isLoading = false;
        });
    },
    openChangeRequestForm(store) {
      this.changeRequestForm = {
        ...this.changeRequestForm,
        store,
        isOpen: true,
      };
    },
    requestStoreUpdate(formValues) {
      this.changeRequestForm.isLoading = true;
      const url = storeRequest(this.changeRequestForm.store.id);
      const payload = {
        status: 'update',
        new_address: {
          street: formValues.address,
        },
        new_coordinates: {
          latitude: formValues.latitude,
          longitude: formValues.longitude,
        },
      };

      this.axios
        .post(url, payload)
        .then(() => {
          this.resetChangeRequestForm();
          this.displaySnackbar({
            text: 'Successfully requested to update store.',
            color: 'green',
          });
        })
        .catch(() => {
          this.resetChangeRequestForm();
          this.displaySnackbar({
            text: 'An error occured. Please try again',
            color: 'red',
          });
        });
    },
    requestStoreRemove(formValues) {
      this.changeRequestForm.isLoading = true;
      const url = storeRequest(this.changeRequestForm.store.id);
      const payload = {
        status: 'delete',
        description: formValues.reasonToRemove,
      };

      this.axios
        .post(url, payload)
        .then(() => {
          this.resetChangeRequestForm();
          this.displaySnackbar({
            text: 'Successfully requested to delete store.',
            color: 'green',
          });
        })
        .catch(() => {
          this.resetChangeRequestForm();
          this.displaySnackbar({
            text: 'An error occured. Please try again',
            color: 'red',
          });
        });
    },
    resetChangeRequestForm() {
      this.changeRequestForm = {
        isOpen: false,
        store: undefined,
        isLoading: false,
      };
    },
    displaySnackbar({ text, color }) {
      this.snackbar = {
        text,
        color,
        isOpen: true,
      };
    },
  },
};
</script>

<style scoped>
td.md {
  min-width: 110px;
}
td.lg {
  min-width: 200px;
}
td.s-lg {
  min-width: 130px;
}
* {
  scrollbar-width: thin;
}
::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}
::-webkit-scrollbar-track {
  border-radius: 10px;
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.2);
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.2);
}
::-webkit-scrollbar-thumb {
  border-radius: 10px;
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.4);
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.4);
}
</style>
